import { Navigate, useSearch } from '@tanstack/react-router';
import { YemboLogo } from '@yembo/yemblocks-core';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { MfaEmployee, MfaChannel } from 'src/queries/Login';
import { useAuth } from 'src/util/AuthModule';
import { clearPendingLogin, getPendingLogin, setPendingLogin } from './_util/pendingLogin';
import { ForgotPassword } from './ForgotPassword';
import { LoginForm } from './LoginForm';
import { ResendMfa } from './ResendMfa';
import { VerifyBackupCode } from './VerifyBackupCode';
import { VerifyMfaForm } from './VerifyMfaForm';

export type LoginFormData = {
  email?: string;
  isEmailValid?: boolean;
  password?: string;
  backupCode?: string;
  mfaChannel?: MfaChannel;
  mfaEmployee?: MfaEmployee;
};

const isFirstStep = (pathname: string) => pathname === '/' || pathname === '/login';

const Login = () => {
  const [formData, setFormDataState] = useState<LoginFormData>(() => {
    if (isFirstStep(window.location.pathname)) clearPendingLogin();
    return getPendingLogin();
  });

  const setFormData: Dispatch<SetStateAction<LoginFormData>> = useCallback((action) => {
    setFormDataState((previous) => setPendingLogin(typeof action === 'function' ? action(previous) : action));
  }, []);

  // biome-ignore lint/suspicious/noExplicitAny: Router type migration in progress
  const searchParams = useSearch({ strict: false }) as any;
  const { accessToken, deviceKey, employeeKey } = useAuth();
  const isLogged = accessToken && deviceKey && employeeKey;

  if (isLogged) {
    const nextPage = searchParams?.nextPage;

    return <Navigate to={nextPage || '/jobs'} replace />;
  }

  const { pathname } = window.location;

  const loginMfaForms: Record<string, React.JSX.Element> = {
    '/verify-mfa': <VerifyMfaForm formData={formData} />,
    '/verify-backup-code': <VerifyBackupCode formData={formData} />,
    '/resend-mfa': <ResendMfa formData={formData} setFormData={setFormData} />,
    '/forgot-password': <ForgotPassword formData={formData} setFormData={setFormData} />,
  };

  const loginMfaForm = loginMfaForms[pathname] ?? <LoginForm formData={formData} setFormData={setFormData} />;

  return (
    <div className='login-mfa authentication'>
      <div className='card-area'>
        <div className='card'>
          <YemboLogo />
          {loginMfaForm}
        </div>
      </div>
      <div className='auth-footer'>
        <p className='legal-text'>
          {'By continuing, you agree to our '}
          <span>End User Terms of Use </span>
          {'and '}
          <span>Privacy Policy</span>
          {'. For support and product improvement purposes your session may be recorded. For more information, '}
          <a href='https://logrocket.com/privacy/' target='_blank' rel='noreferrer'>
            LogRocket&#39;s privacy policy
          </a>
          {'. This software is '}
          <a href='https://www.yembo.ai/legal/patents' target='_blank' rel='noreferrer'>
            patent protected
          </a>
          .
        </p>
        <div className='footer-links'>
          <span>&copy;{new Date().getFullYear()} Yembo, Inc.</span>
          <a href='https://www.yembo.ai/legal/terms' target='_blank' rel='noreferrer'>
            Terms and conditions
          </a>
          <a href='https://www.yembo.ai/legal/privacy-policy' target='_blank' rel='noreferrer'>
            Privacy
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
